/**
 * May this organization still create NATIVE Vantio invoices? (INV-RETIRE.1b)
 *
 * ONE HOOK, TWO CONSUMERS, ONE ANSWER. Both the invoice list (which offers "Neue
 * Rechnung" and the monthly run) and the create wizard itself have to gate on the
 * same fact. Written twice, the two would eventually disagree — and the way they
 * would disagree is the worst one available: a page that offers a button the next
 * page refuses. So the fetch, the state and the fail-closed rule live here once.
 *
 * FAIL CLOSED, AND THAT IS THE WHOLE DESIGN. `nativeCreationEnabled` is true ONLY
 * when the backend has said so. While loading it is false; when the request failed
 * it is false; if the response is malformed it is false. The tempting shape —
 *
 *     capabilities?.native_invoice_creation_enabled ?? true
 *
 * — is fail-OPEN: a network blip would put a create button in front of an operator
 * whose organization bills in Bexio. The backend would still refuse the write, so
 * nothing corrupt gets stored, but the operator would fill in a whole wizard to
 * earn a 409. Hiding a control that should have been there is a smaller failure
 * than offering one that cannot work.
 *
 * THE BOOLEAN IS THE CAPABILITY, NOT THE PROVIDER STRING. The backend sends both
 * `invoice_creation_provider` and `native_invoice_creation_enabled`; gating reads
 * only the boolean. `provider !== "bexio"` would be a second, local derivation of
 * the same decision, and it silently becomes wrong the day a third provider exists.
 * The provider string is carried through for display and diagnostics.
 *
 * NOT DERIVED FROM ANYTHING ELSE. Not from the Bexio connection's presence or
 * status, not from the Bexio settings screen, not from the organization name, the
 * URL or the user's role. A connection is a capability; the provider is an
 * intention, and the two are deliberately separate all the way down to the column.
 *
 * NOT READ FROM `GET /api/admin/billing-settings`. That endpoint carries the same
 * field but is admin-only, and managers create invoices too.
 */
import { useCallback, useEffect, useState } from "react";

import { fetchInvoiceCapabilities } from "../../../api/adminData";

export default function useInvoiceCapabilities() {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError("");
    fetchInvoiceCapabilities()
      .then((res) => {
        if (!alive) return;
        setCapabilities(res);
      })
      .catch((e) => {
        if (!alive) return;
        // The stored value is dropped rather than kept: a stale "enabled" from an
        // earlier successful read must not survive a later failure.
        setCapabilities(null);
        setError(
          e?.message || "Rechnungs-Berechtigungen konnten nicht geladen werden."
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  return {
    capabilities,
    loading,
    error,
    reload: load,
    // Strict `=== true`. Anything else — null, undefined, a string, a loading or
    // failed read — is not permission.
    nativeCreationEnabled: capabilities?.native_invoice_creation_enabled === true,
    provider: capabilities?.invoice_creation_provider ?? null,
  };
}
